import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { X, Smartphone, Star, Zap, Download, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

export default function AppDownloadPopup() {
  const [show, setShow] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Don't show on mobile webview (already in app) or if already dismissed
    const isNative = window.ReactNativeWebView;
    const wasDismissed = sessionStorage.getItem('app_download_dismissed');
    if (isNative || wasDismissed) return;

    const load = async () => {
      try {
        const res = await axios.get(`${API}/campaigns/popup-settings`);
        const s = res.data;
        setSettings(s);
        if (!s?.show_app_download) return;

        // Show after delay
        const delay = (s.app_download_delay_seconds || 60) * 1000;
        const timer = setTimeout(() => setShow(true), delay);
        return () => clearTimeout(timer);
      } catch {}
    };
    load();
  }, []);

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem('app_download_dismissed', '1');
  };

  if (!show || !settings?.show_app_download) return null;

  const appUrl = settings.app_download_url || 'https://play.google.com/store/apps/details?id=com.darasani.app';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-testid="app-download-popup">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header gradient */}
        <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8 text-center text-white">
          <button onClick={dismiss} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors" data-testid="dismiss-app-download">
            <X className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-black mb-2">Get the Darasani App</h2>
          <p className="text-indigo-100 text-sm">
            {settings.app_download_message || 'Enjoy a richer learning experience with offline access, push notifications, and more!'}
          </p>
        </div>

        {/* Features */}
        <div className="p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
              <Zap className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Faster & Smoother</p>
              <p className="text-xs text-slate-500">Native performance for all features</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <Download className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Offline Access</p>
              <p className="text-xs text-slate-500">Download ebooks & courses for offline study</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
              <Star className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Push Notifications</p>
              <p className="text-xs text-slate-500">Get notified about live classes & updates</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 space-y-2.5">
          <a href={appUrl} target="_blank" rel="noopener noreferrer" className="block" data-testid="download-app-btn">
            <Button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3 rounded-xl text-base shadow-lg shadow-indigo-500/25">
              <Download className="w-5 h-5 mr-2" />
              Download the App
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </a>
          <button
            onClick={dismiss}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-600 transition-colors py-1"
            data-testid="continue-web-btn"
          >
            Continue on web
          </button>
        </div>
      </div>
    </div>
  );
}
